import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Button, Paper } from '@mui/material';
import { PersonAddAlt } from '@mui/icons-material';
import { sendParentInvitation } from '../services/authService';
import { getProfile } from '../services/authSlice';
import SentInvitationsList from './SentInvitationsList';
import ReceivedInvitationsList from './ReceivedInvitationsList';

function InviteParentSection({ onInvitationSent }) {
  const { t } = useTranslation();
  const { account } = useSelector((state) => state.auth);
  const children = useMemo(
    () => (account?.children || []).filter((c) => !c.role || c.role === 'primary'),
    [account?.children],
  );
  const childrenKey = children.map((c) => c.id).join(',');
  const [email, setEmail] = useState('');
  const [selectedChildIds, setSelectedChildIds] = useState(
    () => children.map((c) => c.id),
  );
  const [sending, setSending] = useState(false);
  const [message, setMessage] = useState(null);
  const [messageType, setMessageType] = useState('success');

  useEffect(() => {
    setSelectedChildIds(children.map((c) => c.id));
  }, [childrenKey]);

  const toggleChild = (childId) => {
    setSelectedChildIds((prev) => (prev.includes(childId)
      ? prev.filter((id) => id !== childId)
      : [...prev, childId]));
  };

  const handleSend = async () => {
    if (!email || selectedChildIds.length === 0) return;
    setSending(true);
    setMessage(null);
    try {
      await sendParentInvitation(email, selectedChildIds);
      setMessage(t('pages.profile.invite.sentSuccess'));
      setMessageType('success');
      setEmail('');
      setSelectedChildIds(children.map((c) => c.id));
      if (onInvitationSent) onInvitationSent();
    } catch (err) {
      setMessage(err.response?.data?.error || t('pages.profile.invite.sentError'));
      setMessageType('danger');
    } finally {
      setSending(false);
    }
  };

  if (children.length === 0) {
    return null;
  }

  return (
    <Paper sx={{ p: 2, mb: 3 }}>
      <div>
        <div>
          <h4 className="mb-3">
            {t('pages.profile.inviteParent')}
          </h4>
          <p className="text-muted small mb-3">
            {t('pages.profile.inviteParentText')}
          </p>
          <div className="mb-3">
            <span className="form-label fw-semibold d-block">{t('pages.profile.invite.selectChildren')}</span>
            {children.map((child) => (
              <div className="form-check" key={child.id}>
                <input
                  className="form-check-input"
                  type="checkbox"
                  id={`child-${child.id}`}
                  checked={selectedChildIds.includes(child.id)}
                  onChange={() => toggleChild(child.id)}
                  disabled={sending}
                />
                <label className="form-check-label" htmlFor={`child-${child.id}`}>
                  {child.nameHU || `${child.lastName} ${child.firstName}`}
                </label>
              </div>
            ))}
          </div>
          <div className="d-flex gap-2">
            <input
              type="email"
              className="form-control"
              style={{ width: '80%' }}
              placeholder="E-mail cím"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={sending}
            />
            <Button
              size="small"
              variant="contained"
              component="label"
              startIcon={<PersonAddAlt />}
              onClick={handleSend}
              disabled={sending || !email || selectedChildIds.length === 0}
            >
              {sending ? t('pages.profile.invite.sendingInProgress') : t('pages.profile.invite.sending')}
            </Button>
          </div>
          {selectedChildIds.length === 0 && (
            <div className="text-danger small mt-1">
              {t('pages.profile.invite.chooseOne')}
            </div>
          )}
          {message && (
            <div className={`alert alert-${messageType} mt-2 mb-0`} role="alert">
              {message}
            </div>
          )}
        </div>
      </div>
    </Paper>
  );
}

InviteParentSection.propTypes = {
  onInvitationSent: PropTypes.func,
};

InviteParentSection.defaultProps = {
  onInvitationSent: null,
};

function InvitationsForm() {
  const dispatch = useDispatch();
  const { account, loading } = useSelector((state) => state.auth);
  const [invitationRefreshKey, setInvitationRefreshKey] = useState(0);

  useEffect(() => {
    dispatch(getProfile());
  }, [dispatch]);

  const handleInvitationSent = () => {
    setInvitationRefreshKey((prev) => prev + 1);
    dispatch(getProfile());
  };

  if (!account || loading === 'pending') {
    return null;
  }

  return (
    <div>
      <InviteParentSection onInvitationSent={handleInvitationSent} />
      <SentInvitationsList refreshKey={invitationRefreshKey} />
      <ReceivedInvitationsList refreshKey={invitationRefreshKey} />
    </div>
  );
}

export default InvitationsForm;
